class ProductHero {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('form[data-product-form]') || container.querySelector('form');
    this.productData = this.getProductData();
    this.variantSelect = container.querySelector('.product-hero__variant-select');
    this.quantityInput = container.querySelector('.product-hero__quantity-input');
    this.currentVariant = this.getInitialVariant();
    this.currentSwatchVariant = null;

    this.setupGallery();
    this.setupMobileSlider();
    this.setupDesktopGallery();
    this.setupVariantSelectors();
    this.setupQuantityControls();
    this.setupAddToCart();
    this.setupSwatchButton();
    this.setupPrimerReveal();
    this.setupPrimerQuantityControls();
    this.setupPrimerAddButton();
    this.setupCalculator();
    this.setupAccordion();
    this.setupTooltips();
    this.applyVariant(this.currentVariant, { closeColorMenu: false });
  }

  getProductData() {
    const configNode = this.container.querySelector('[data-product-hero-json]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('Unable to parse product hero JSON', error);
      return null;
    }
  }

  getInitialVariant() {
    if (!this.productData || !Array.isArray(this.productData.variants)) return null;

    return this.getVariantById(this.productData.currentVariantId) || this.productData.variants[0] || null;
  }

  getVariantById(variantId) {
    if (!this.productData || !Array.isArray(this.productData.variants)) return null;

    return this.productData.variants.find(variant => String(variant.id) === String(variantId)) || null;
  }

  setupGallery() {
    return;
  }

  setupMobileSlider() {
    const slider = this.container.querySelector('[data-slider]');
    const pagination = this.container.querySelector('[data-pagination]');

    if (!slider || !pagination) return;

    const slides = Array.from(slider.querySelectorAll('.product-hero__slide'));
    const dots = pagination.querySelectorAll('.product-hero__pagination-dot');

    if (slides.length <= 1) return;

    const updateActiveDot = (index) => {
      dots.forEach(dot => dot.classList.remove('product-hero__pagination-dot--active'));
      if (dots[index]) dots[index].classList.add('product-hero__pagination-dot--active');
    };

    const railStart = slides[0].offsetLeft;

    const getActiveIndex = () => {
      const scrollLeft = slider.scrollLeft;
      return slides.reduce((best, slide, index) => (
        Math.abs((slide.offsetLeft - railStart) - scrollLeft) < Math.abs((slides[best].offsetLeft - railStart) - scrollLeft)
          ? index
          : best
      ), 0);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        slider.scrollTo({
          left: slides[index].offsetLeft - railStart,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });
    });

    let scrollTimeout;
    slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        updateActiveDot(getActiveIndex());
      }, 100);
    });

    updateActiveDot(0);
  }

  setupDesktopGallery() {
    const main = this.container.querySelector('[data-desktop-main]');
    const thumbRail = this.container.querySelector('[data-thumbs]');
    const previousButton = this.container.querySelector('[data-gallery-previous]');
    const nextButton = this.container.querySelector('[data-gallery-next]');

    if (!main || !thumbRail) return;

    const slides = Array.from(main.querySelectorAll('.product-hero__main-slide'));
    const thumbs = Array.from(thumbRail.querySelectorAll('.product-hero__thumb'));

    if (thumbs.length <= 1) return;

    const setActive = (index) => {
      const normalizedIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('product-hero__main-slide--active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === normalizedIndex;
        thumb.classList.toggle('product-hero__thumb--active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.scrollThumbIntoView(thumbRail, thumbs[normalizedIndex]);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setActive(index));
    });

    previousButton?.addEventListener('click', () => {
      const currentIndex = thumbs.findIndex(thumb => thumb.classList.contains('product-hero__thumb--active'));
      setActive(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      const currentIndex = thumbs.findIndex(thumb => thumb.classList.contains('product-hero__thumb--active'));
      setActive(currentIndex + 1);
    });

    thumbRail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

      const currentIndex = thumbs.findIndex(thumb => thumb.classList.contains('product-hero__thumb--active'));
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;

      event.preventDefault();
      setActive(nextIndex);
      thumbs[nextIndex].focus();
    });

    setActive(0);
  }

  scrollThumbIntoView(rail, thumb) {
    if (!thumb) return;

    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const offset = (thumbRect.left - railRect.left) - (railRect.width - thumbRect.width) / 2;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  setupVariantSelectors() {
    this.colorTrigger = this.container.querySelector('[data-color-trigger]');
    this.colorMenu = this.container.querySelector('[data-color-menu]');
    this.optionButtons = Array.from(this.container.querySelectorAll('[data-option-value][data-option-position]'));

    if (this.colorTrigger && this.colorMenu) {
      this.colorTrigger.addEventListener('click', () => {
        const isOpen = this.colorTrigger.getAttribute('aria-expanded') === 'true';
        this.toggleColorMenu(!isOpen);
      });
    }

    this.optionButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const position = button.dataset.optionPosition;
        const value = button.dataset.optionValue;
        this.selectOption(position, value);

        if (button.dataset.optionType === 'color') {
          this.closeColorMenu();
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.colorTrigger || !this.colorMenu) return;

      const colorGroup = this.colorTrigger.closest('.product-hero__selector-group--color');
      if (colorGroup && colorGroup.contains(event.target)) return;

      this.closeColorMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeColorMenu();
    });
  }

  toggleColorMenu(forceOpen) {
    if (!this.colorTrigger || !this.colorMenu) return;

    const shouldOpen = typeof forceOpen === 'boolean'
      ? forceOpen
      : this.colorTrigger.getAttribute('aria-expanded') !== 'true';

    this.colorTrigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.colorMenu.hidden = !shouldOpen;
    this.container.classList.toggle('product-hero--color-open', shouldOpen);
  }

  closeColorMenu() {
    this.toggleColorMenu(false);
  }

  selectOption(position, value) {
    const nextSelections = {
      ...this.getSelectedOptions(),
      [position]: value
    };

    let nextVariant = this.findAvailableVariant(nextSelections);

    if (!nextVariant) {
      nextVariant = this.findFirstAvailableVariantForSelection(position, value);
    }

    if (!nextVariant) {
      nextVariant = this.findMatchingVariant(nextSelections) || this.getVariantById(this.variantSelect?.value);
    }

    if (nextVariant) {
      this.applyVariant(nextVariant);
    }
  }

  getSelectedOptions() {
    if (this.currentVariant && this.currentVariant.options) {
      return { ...this.currentVariant.options };
    }

    const selectedOptions = {};
    const selectedButtons = this.container.querySelectorAll('.is-selected[data-option-position][data-option-value]');

    selectedButtons.forEach(button => {
      selectedOptions[button.dataset.optionPosition] = button.dataset.optionValue;
    });

    return selectedOptions;
  }

  findMatchingVariant(selections, { availableOnly = false } = {}) {
    if (!this.productData || !Array.isArray(this.productData.variants)) return null;

    return this.productData.variants.find(variant => {
      if (availableOnly && !variant.available) return false;

      return Object.entries(selections).every(([position, value]) => (
        !value || variant.options?.[position] === value
      ));
    }) || null;
  }

  findAvailableVariant(selections) {
    return this.findMatchingVariant(selections, { availableOnly: true });
  }

  findFirstAvailableVariantForSelection(position, value) {
    if (!this.productData || !Array.isArray(this.productData.variants)) return null;

    return this.productData.variants.find(variant => (
      variant.available && variant.options?.[position] === value
    )) || null;
  }

  applyVariant(variant, { closeColorMenu = true } = {}) {
    if (!variant) return;

    this.currentVariant = variant;

    if (this.variantSelect) {
      this.variantSelect.value = String(variant.id);
    }

    this.syncSelectedOptions(variant);
    this.syncOptionAvailability();
    this.syncColorSummary();
    this.updatePricing(variant);
    this.updateAddToCartButton();
    this.updateSwatchButton();

    if (closeColorMenu) {
      this.closeColorMenu();
    }
  }

  syncSelectedOptions(variant) {
    const optionGroups = this.container.querySelectorAll('[data-option-position][data-option-type]');

    optionGroups.forEach(group => {
      const position = group.dataset.optionPosition;
      const selectedValue = variant.options?.[position];
      const buttons = group.querySelectorAll('[data-option-value]');

      buttons.forEach(button => {
        const isSelected = button.dataset.optionValue === selectedValue;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    });
  }

  syncOptionAvailability() {
    const selectedOptions = this.getSelectedOptions();

    this.optionButtons.forEach(button => {
      const position = button.dataset.optionPosition;
      const type = button.dataset.optionType;
      const value = button.dataset.optionValue;
      const candidateSelections = { ...selectedOptions, [position]: value };
      const matchingVariants = this.getMatchingVariants(candidateSelections);
      const isNotAvailable = matchingVariants.length === 0;
      const isAvailable = matchingVariants.some(variant => variant.available);
      const isComingSoon = type === 'color' && !isAvailable && matchingVariants.some(variant => variant.comingSoon);
      const badge = button.querySelector('[data-coming-soon-badge]');

      button.hidden = isNotAvailable;
      button.disabled = isNotAvailable || !isAvailable;
      button.classList.toggle('is-disabled', isNotAvailable || !isAvailable);
      button.classList.toggle('is-coming-soon', isComingSoon);

      if (badge) {
        badge.hidden = !isComingSoon;
      }
    });
  }

  getMatchingVariants(selections) {
    if (!this.productData || !Array.isArray(this.productData.variants)) return [];

    return this.productData.variants.filter(variant => (
      Object.entries(selections).every(([position, value]) => (
        !value || variant.options?.[position] === value
      ))
    ));
  }

  syncColorSummary() {
    if (!this.productData || !this.productData.colorOptionPosition) return;

    const colorValue = this.currentVariant?.options?.[String(this.productData.colorOptionPosition)];
    if (!colorValue) return;

    const escapedValue = this.escapeSelectorValue(colorValue);
    const selectedRow = this.container.querySelector(`.product-hero__color-row[data-option-value="${escapedValue}"]`);
    if (!selectedRow) return;

    const nameTarget = this.container.querySelector('[data-selected-color-name]');
    const descriptionTarget = this.container.querySelector('[data-selected-color-description]');
    const swatchTarget = this.container.querySelector('[data-selected-color-swatch]');
    const description = selectedRow.dataset.optionDescription || '';

    if (nameTarget) {
      nameTarget.textContent = colorValue;
    }

    if (descriptionTarget) {
      descriptionTarget.textContent = description;
      descriptionTarget.hidden = !description;
    }

    if (swatchTarget) {
      this.applySwatchPresentation(swatchTarget, {
        color: selectedRow.dataset.swatchColor || '',
        image: selectedRow.dataset.swatchImage || ''
      });
    }
  }

  applySwatchPresentation(node, { color = '', image = '' } = {}) {
    if (!node) return;

    node.style.backgroundImage = image ? `url("${image}")` : '';
    node.style.backgroundColor = !image && color ? color : '';

    if (color) {
      node.dataset.swatchColor = color;
    } else {
      delete node.dataset.swatchColor;
    }

    if (image) {
      node.dataset.swatchImage = image;
    } else {
      delete node.dataset.swatchImage;
    }
  }

  updatePricing(variant) {
    if (!variant) return;

    const titlePriceElements = this.container.querySelectorAll('[data-title-price]');
    const titleCompareElements = this.container.querySelectorAll('[data-title-price-compare]');
    const currentPriceText = this.formatMoney(variant.price || 0);
    const hasComparePrice = Boolean(variant.compareAtPrice && variant.compareAtPrice > variant.price);
    const comparePriceText = hasComparePrice ? this.formatMoney(variant.compareAtPrice) : '';

    titlePriceElements.forEach((element) => {
      element.textContent = currentPriceText;
    });

    titleCompareElements.forEach((element) => {
      element.textContent = comparePriceText;
      element.classList.toggle('product-hero__title-price-compare--hidden', !hasComparePrice);
    });

    this.syncPurchaseSummary(variant);
  }

  setupQuantityControls() {
    const decreaseBtn = this.container.querySelector('[data-quantity="decrease"]');
    const increaseBtn = this.container.querySelector('[data-quantity="increase"]');
    const quantityInput = this.quantityInput;

    if (!quantityInput) return;

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value, 10) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
          this.syncPurchaseSummary(this.currentVariant);
          this.updateAddToCartButton();
        }
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value, 10) || 1;
        quantityInput.value = currentValue + 1;
        this.syncPurchaseSummary(this.currentVariant);
        this.updateAddToCartButton();
      });
    }

    quantityInput.addEventListener('change', () => {
      const value = parseInt(quantityInput.value, 10) || 1;
      quantityInput.value = Math.max(1, value);
      this.syncPurchaseSummary(this.currentVariant);
      this.updateAddToCartButton();
    });
  }

  setupAddToCart() {
    if (!this.form) return;

    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      await this.handleAddToCart();
    });
  }

  setupSwatchButton() {
    const swatchButton = this.container.querySelector('[data-swatch-add]');

    if (!swatchButton) return;

    swatchButton.addEventListener('click', async () => {
      if (swatchButton.disabled) return;
      await this.handleAddSwatch();
    });
  }

  setupPrimerReveal() {
    const primerTrigger = this.container.querySelector('[data-primer-trigger]');
    const primerPanel = this.container.querySelector('[data-primer-panel]');

    if (!primerTrigger || !primerPanel) return;

    this.primerTrigger = primerTrigger;
    this.primerPanel = primerPanel;

    primerTrigger.addEventListener('click', () => {
      const isOpen = primerTrigger.getAttribute('aria-expanded') === 'true';
      this.togglePrimerPanel(!isOpen);
    });

    document.addEventListener('click', (event) => {
      if (!this.primerTrigger || !this.primerPanel) return;
      if (this.primerTrigger.contains(event.target) || this.primerPanel.contains(event.target)) return;

      this.closePrimerPanel();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closePrimerPanel();
    });
  }

  togglePrimerPanel(forceOpen) {
    if (!this.primerTrigger || !this.primerPanel) return;

    const shouldOpen = typeof forceOpen === 'boolean'
      ? forceOpen
      : this.primerTrigger.getAttribute('aria-expanded') !== 'true';

    this.primerTrigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.primerPanel.hidden = !shouldOpen;
  }

  closePrimerPanel() {
    this.togglePrimerPanel(false);
  }

  setupPrimerQuantityControls() {
    const decreaseBtn = this.container.querySelector('[data-primer-quantity="decrease"]');
    const increaseBtn = this.container.querySelector('[data-primer-quantity="increase"]');
    const quantityInput = this.container.querySelector('[data-primer-quantity-input]');

    if (!quantityInput) return;

    this.primerQuantityInput = quantityInput;

    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value, 10) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      });
    }

    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value, 10) || 1;
        quantityInput.value = currentValue + 1;
      });
    }

    quantityInput.addEventListener('change', () => {
      const value = parseInt(quantityInput.value, 10) || 1;
      quantityInput.value = Math.max(1, value);
    });
  }

  setupPrimerAddButton() {
    const primerButton = this.container.querySelector('[data-primer-add]');

    if (!primerButton) return;

    primerButton.addEventListener('click', async () => {
      if (primerButton.disabled) return;
      await this.handleAddPrimer();
    });
  }

  updateAddToCartButton() {
    const addToCartBtn = this.container.querySelector('.product-hero__add-to-cart-btn');
    const addToCartText = this.container.querySelector('.product-hero__add-to-cart-text');
    const quantityInput = this.quantityInput;

    if (!addToCartBtn || !quantityInput) return;

    const quantity = this.getQuantity();
    const canPurchase = Boolean(this.currentVariant?.available) && quantity > 0;

    addToCartBtn.disabled = !canPurchase;
    addToCartBtn.classList.toggle('product-hero__add-to-cart-btn--disabled', !canPurchase);

    if (addToCartText) {
      const defaultTemplate = addToCartText.dataset.defaultTextTemplate || 'Add To Cart - [price]';
      const unavailableText = addToCartText.dataset.unavailableText || 'Sold Out';
      const totalPriceText = this.formatMoney((this.currentVariant?.price || 0) * quantity);

      addToCartText.textContent = canPurchase
        ? this.renderTemplate(defaultTemplate, { price: totalPriceText, quantity })
        : unavailableText;
    }
  }

  updateSwatchButton() {
    const swatchButton = this.container.querySelector('[data-swatch-add]');

    if (!swatchButton) return;

    const swatchVariant = this.resolveSwatchVariant();
    this.currentSwatchVariant = swatchVariant;
    swatchButton.disabled = !swatchVariant;
  }

  resolveSwatchVariant() {
    if (!this.productData?.swatchProduct?.configured) return null;

    const swatchProduct = this.productData.swatchProduct;
    const colorPosition = String(this.productData.colorOptionPosition || '');
    const colorValue = this.currentVariant?.options?.[colorPosition];

    if (swatchProduct.colorOptionPosition) {
      const swatchColorPosition = String(swatchProduct.colorOptionPosition);
      return swatchProduct.variants.find(variant => (
        variant.available && variant.options?.[swatchColorPosition] === colorValue
      )) || null;
    }

    if (swatchProduct.defaultVariantId) {
      return swatchProduct.variants.find(variant => (
        variant.available && String(variant.id) === String(swatchProduct.defaultVariantId)
      )) || null;
    }

    return null;
  }

  async handleAddToCart() {
    const addToCartBtn = this.container.querySelector('.product-hero__add-to-cart-btn');
    const addToCartText = this.container.querySelector('.product-hero__add-to-cart-text');
    const quantity = this.getQuantity();

    if (!this.currentVariant) return;

    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.classList.add('product-hero__add-to-cart-btn--loading');
    }

    if (addToCartText) {
      addToCartText.textContent = addToCartText.dataset.addingText || 'Adding...';
    }

    try {
      await this.addItemsToCart([{ id: this.currentVariant.id, quantity }]);
      this.showAddToCartSuccess();
    } catch (error) {
      this.showAddToCartError(error.message);
    } finally {
      if (addToCartBtn) {
        addToCartBtn.classList.remove('product-hero__add-to-cart-btn--loading');
      }

      this.updateAddToCartButton();
    }
  }

  async handleAddSwatch() {
    const swatchButton = this.container.querySelector('[data-swatch-add]');

    if (!this.currentSwatchVariant) {
      this.showCartError('That swatch color is currently unavailable.');
      return;
    }

    const originalText = swatchButton?.textContent || '';

    if (swatchButton) {
      swatchButton.disabled = true;
      swatchButton.textContent = 'Adding...';
      swatchButton.classList.add('is-loading');
    }

    try {
      await this.addItemsToCart([{ id: this.currentSwatchVariant.id, quantity: 1 }]);
    } catch (error) {
      this.showCartError(error.message);
    } finally {
      this.updateSwatchButton();

      if (swatchButton) {
        swatchButton.textContent = originalText;
        swatchButton.classList.remove('is-loading');
      }
    }
  }

  async handleAddPrimer() {
    const primerButton = this.container.querySelector('[data-primer-add]');
    const primerButtonText = this.container.querySelector('[data-primer-add-text]');
    const primerVariantId = this.productData?.primerProduct?.defaultVariantId;
    const quantity = Math.max(1, parseInt(this.primerQuantityInput?.value, 10) || 1);

    if (!primerVariantId) {
      this.showCartError('Primer is currently unavailable.');
      return;
    }

    if (primerButton) {
      primerButton.disabled = true;
      primerButton.classList.add('is-loading');
    }

    if (primerButtonText) {
      primerButtonText.textContent = 'Adding...';
    }

    try {
      await this.addItemsToCart([{ id: primerVariantId, quantity }]);
      this.showAddPrimerSuccess(primerButtonText);
    } catch (error) {
      this.showAddPrimerError(primerButtonText, error.message);
    } finally {
      if (primerButton) {
        primerButton.disabled = false;
        primerButton.classList.remove('is-loading');
      }
    }
  }

  showAddPrimerSuccess(primerButtonText) {
    if (!primerButtonText) return;

    primerButtonText.textContent = 'Added!';
    window.setTimeout(() => {
      primerButtonText.textContent = 'Add to Cart';
    }, 2000);
  }

  showAddPrimerError(primerButtonText, message) {
    if (primerButtonText) {
      primerButtonText.textContent = 'Error - Try Again';
      window.setTimeout(() => {
        primerButtonText.textContent = 'Add to Cart';
      }, 3000);
    }

    this.showCartError(message);
  }

  async addItemsToCart(items) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.addItemsAndOpen === 'function') {
      return cartDrawer.addItemsAndOpen(items);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const result = await response.json();

    if (!response.ok) {
      const errorMessage = result.description || result.message || 'Failed to add to cart';
      throw new Error(errorMessage);
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { items: result.items || result }
    }));

    if (cartDrawer) {
      if (typeof cartDrawer.refreshCart === 'function') {
        await cartDrawer.refreshCart();
      }

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    return result;
  }

  showAddToCartSuccess() {
    const addToCartText = this.container.querySelector('.product-hero__add-to-cart-text');

    if (addToCartText) {
      addToCartText.textContent = addToCartText.dataset.addedText || 'Added!';
      window.setTimeout(() => {
        this.updateAddToCartButton();
      }, 2000);
    }
  }

  showAddToCartError(message = 'Failed to add to cart') {
    const addToCartText = this.container.querySelector('.product-hero__add-to-cart-text');

    if (addToCartText) {
      addToCartText.textContent = addToCartText.dataset.errorText || 'Error - Try Again';
      window.setTimeout(() => {
        this.updateAddToCartButton();
      }, 3000);
    }

    this.showCartError(message);
  }

  showCartError(message = 'Failed to add to cart') {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.showError === 'function') {
      cartDrawer.showError(message);
      return;
    }

    this.showNotification(message, 'error');
  }

  showNotification(message, type = 'info') {
    let notification = document.querySelector('.product-hero__notification');

    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'product-hero__notification';
      notification.setAttribute('role', 'alert');
      notification.setAttribute('aria-live', 'polite');
      document.body.appendChild(notification);
    }

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'error' ? '#dc3545' : '#0F5F21'};
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 10001;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      opacity: 1;
      transition: opacity 0.3s ease;
      max-width: 90%;
      text-align: center;
    `;
    notification.textContent = message;

    clearTimeout(this.notificationTimeout);
    this.notificationTimeout = window.setTimeout(() => {
      notification.style.opacity = '0';
    }, 4000);
  }

  setupCalculator() {
    const calculatorLink = this.container.querySelector('.product-hero__calculator-link');

    if (calculatorLink) {
      calculatorLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.openCalculator();
      });
    }
  }

  setupAccordion() {
    const accordionHeaders = this.container.querySelectorAll('.product-hero__accordion-header');

    accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const accordionItem = header.closest('.product-hero__accordion-item');
        const content = accordionItem.querySelector('.product-hero__accordion-content');
        const icon = header.querySelector('.product-hero__accordion-icon');
        const isExpanded = content.classList.contains('product-hero__accordion-content--expanded');

        header.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');

        if (isExpanded) {
          content.classList.remove('product-hero__accordion-content--expanded');
          icon.classList.remove('product-hero__accordion-icon--expanded');
          content.style.maxHeight = null;
        } else {
          content.classList.add('product-hero__accordion-content--expanded');
          icon.classList.add('product-hero__accordion-icon--expanded');
          content.style.maxHeight = `${content.scrollHeight}px`;
        }
      });
    });
  }

  setupTooltips() {
    const helpTooltip = this.container.querySelector('.product-hero__finish-help');

    if (helpTooltip && typeof tippy === 'function') {
      tippy(helpTooltip, {
        content: helpTooltip.dataset.tippyContent,
      });
    }
  }

  openCalculator() {
    window.calculatorModal?.openModal();
  }

  getQuantity() {
    return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
  }

  syncPurchaseSummary(variant) {
    if (!variant) return;

    const quantity = this.getQuantity();
    const totalPriceText = this.formatMoney((variant.price || 0) * quantity);
    const titleNode = this.container.querySelector('[data-quantity-card-title]');
    const descriptionNode = this.container.querySelector('[data-quantity-card-description]');

    if (titleNode) {
      titleNode.textContent = this.renderTemplate(titleNode.dataset.titleTemplate || '', {
        quantity,
        price: totalPriceText
      });
    }

    if (descriptionNode) {
      descriptionNode.textContent = this.renderTemplate(descriptionNode.dataset.descriptionTemplate || '', {
        quantity,
        price: totalPriceText
      });
    }
  }

  renderTemplate(template, values = {}) {
    return Object.entries(values).reduce((result, [key, value]) => (
      result.split(`[${key}]`).join(String(value ?? ''))
    ), template || '');
  }

  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const isWholeAmount = Number.isInteger(amount);
    const locale = this.productData?.moneyLocale || document.documentElement.lang || 'en-US';
    const currency = this.productData?.currencyCode || 'USD';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: isWholeAmount ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      const fixedAmount = isWholeAmount ? amount.toFixed(0) : amount.toFixed(2);
      return `$${fixedAmount}`;
    }
  }

  escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const productHeroContainers = document.querySelectorAll('.product-hero');

  productHeroContainers.forEach(container => {
    new ProductHero(container);
  });
});

window.ProductHero = ProductHero;
